import { ZOOM_EMBED_STYLE, ZOOM_PREFILL_PARAMS } from './zoom.js';

/**
 * Parses a scheduler URL that is safe to put in an `iframe src`, or `null` when it is not one.
 *
 * @remarks TOTAL, never throwing. The booking URL is operator-authored configuration that reaches
 * the browser unvalidated for SHAPE (the FFS pair comes from the `FFS_PROGRAM_SCHEDULE_CONFIG` SSM
 * JSON, and the backend's `parseProgramEntry` checks only non-empty-after-trim), so a value like
 * `scheduler.zoom.us/page` — no scheme — is reachable. `new URL()` throws on it, and this runs
 * during render, so an exception here unmounts the whole React tree: blank page, no header, no
 * footer, and the group-consults tab gone too even though it needs no scheduler URL at all.
 * Returning `null` keeps that a per-frame degradation instead of an app-wide outage.
 *
 * Non-http(s) schemes are rejected as well. They PARSE — `javascript:…` is a perfectly valid URL —
 * so a scheme check is what keeps a hostile or fat-fingered config value out of an `iframe src`.
 * `http:` stays allowed for a local mock scheduler; Zoom itself is https-only.
 */
function parseEmbeddableUrl(bookingUrl: string): URL | null {
  let parsed: URL;
  try {
    parsed = new URL(bookingUrl);
  } catch {
    return null;
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed : null;
}

/**
 * True when `bookingUrl` is an absolute http(s) URL — the same test {@link buildBookingUrl} applies
 * before embedding.
 *
 * @remarks Exported for the NON-iframe affordances a caller renders when embedding is impossible.
 * A schemeless operator value such as `scheduler.zoom.us/init-page` is a RELATIVE url, so putting it
 * in an anchor `href` resolves it against the app's own origin: the member gets
 * `https://consult.<apex>/scheduler.zoom.us/init-page`, which the SPA's 403/404 → `/index.html`
 * rewrite serves as HTTP 200 with the app shell. A new tab showing the same broken app is worse than
 * a dead link, because it looks like it worked. Callers must gate any `href` on this.
 */
export function isEmbeddableBookingUrl(bookingUrl: string): boolean {
  return parseEmbeddableUrl(bookingUrl) !== null;
}

/**
 * Append prefill + Zoom embed params (incl. brand styling) to the booking URL for iframe embed.
 *
 * @returns The URL to embed, or `null` when `bookingUrl` is not an embeddable absolute http(s) URL
 * — see {@link parseEmbeddableUrl}. Callers must render a non-iframe affordance for `null` rather
 * than assume a string.
 */
export function buildBookingUrl(
  bookingUrl: string,
  member: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    origin: string;
  },
): string | null {
  const u = parseEmbeddableUrl(bookingUrl);
  if (u === null) return null;
  u.searchParams.set(ZOOM_PREFILL_PARAMS.email, member.email);
  u.searchParams.set(ZOOM_PREFILL_PARAMS.firstName, member.firstName);
  u.searchParams.set(ZOOM_PREFILL_PARAMS.lastName, member.lastName);
  // Omit entirely when absent — never set to the literal string "undefined", which Zoom would
  // then prefill into the member's phone field on the booking form.
  if (typeof member.phone === 'string' && member.phone !== '') {
    u.searchParams.set(ZOOM_PREFILL_PARAMS.phone, member.phone);
  }
  u.searchParams.set('origin', member.origin); // Zoom embed: parent host (window.location.origin)
  u.searchParams.set('embed', 'true'); // Zoom embed flag
  u.searchParams.set('embedStyle', JSON.stringify(ZOOM_EMBED_STYLE)); // brand colors (Zoom URL-encodes)
  return u.toString();
}
